import { useEffect, useRef, useState } from 'react';

/**
 * Animate a number settling into place. 500ms, ease-out cubic — long enough to
 * register as motion, short enough that nobody waits on it.
 *
 * Runs once per distinct target (a changed value re-animates from the previous
 * displayed value, not from zero), and not at all under
 * `prefers-reduced-motion` — an animated dollar figure is exactly the kind of
 * movement that setting exists to switch off.
 */
export function useCountUp(target: number, ms = 500): number {
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  const first = useRef(true);

  useEffect(() => {
    if (typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      fromRef.current = target;
      setDisplay(target);
      return;
    }
    // First mount animates from 0 so the hero "arrives"; updates from previous.
    const from = first.current ? 0 : fromRef.current;
    first.current = false;
    fromRef.current = target;
    if (from === target) { setDisplay(target); return; }

    let raf = 0;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / ms);
      const eased = 1 - (1 - p) ** 3;
      setDisplay(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);

  return display;
}
